import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { dayjs, duration } from 'date';
import { logger } from 'monitor';

import {
  useApiEvidenceV1DataPartialUpdate,
  useApiInspectionsV1EvidenceUploadCreate,
  useApiInspectionsV1EvidenceUploadUpdate,
  useApiLogisticsV1LoadsEvidenceUploadCreate,
  useApiLogisticsV1LoadsEvidenceUploadUpdate,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadCreate,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadUpdate,
  useApiQualityExternalUploadV1EvidenceUploadCreate,
  useApiQualityExternalUploadV1EvidenceUploadUpdate,
  useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadCreate,
  useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadUpdate,
  useApiSamplesV1SamplesUploadCreate,
  useApiSamplesV1SamplesUploadUpdate,
  useApiTradeV1SiteMaterialsEvidenceUploadCreate,
  useApiTradeV1SiteMaterialsEvidenceUploadUpdate,
} from '../../client/api/api';
import { EvidenceTypeEnum, type ProcessUploadResponse } from '../../schemas';
import { useUploadFile } from '../useUploadFile/useUploadFile';

dayjs.extend(duration);
export const prepareUploadMetrics = (
  photo: File,
  startTime: number,
  uploadStartTime: number,
  fileUploadedTime: number,
  fileProcessingDone: number
) => {
  const fullProcessTime = dayjs.duration(fileProcessingDone - startTime, 'milliseconds').asSeconds();
  const uploadFileDurationMs = Math.max(fileUploadedTime - uploadStartTime, 1);
  const uploadFileTime = dayjs.duration(uploadFileDurationMs, 'milliseconds').asSeconds();
  const photoSizeInBits = photo.size * 8;
  return {
    full_process_time: fullProcessTime,
    // bytes
    size: photo.size,
    upload_file_time: uploadFileTime,
    // bps
    upload_speed: photoSizeInBits / uploadFileTime,
  };
};

export type UploadEvidenceData = {
  photo: File;
  onProgress?: (progress: number) => void;
  parentId: string;
  model?: string;
  startTime: number;
  evidenceType?: EvidenceTypeEnum;
  metadata?: Record<string, unknown>;
  processAsync?: boolean;
};

export type EvidenceUploadMode =
  | 'inspector'
  | 'site-material'
  | 'sample'
  | 'load'
  | 'trade-company-session'
  | 'external-evidence'
  | 'external-load';

export const useUploadEvidence = (
  mode: EvidenceUploadMode,
  mutationOptions?: UseMutationOptions<ProcessUploadResponse, unknown, UploadEvidenceData, unknown>
) => {
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: uploadData } = useApiEvidenceV1DataPartialUpdate();

  const { mutateAsync: generateSampleUploadUrl } = useApiSamplesV1SamplesUploadCreate();
  const { mutateAsync: processSampleFile } = useApiSamplesV1SamplesUploadUpdate();

  const { mutateAsync: generateInspectionUploadUrl } = useApiInspectionsV1EvidenceUploadCreate();
  const { mutateAsync: processInspectionFile } = useApiInspectionsV1EvidenceUploadUpdate();

  const { mutateAsync: generateSiteMaterialUploadUrl } = useApiTradeV1SiteMaterialsEvidenceUploadCreate();
  const { mutateAsync: processSiteMaterialFile } = useApiTradeV1SiteMaterialsEvidenceUploadUpdate();

  const { mutateAsync: generateLoadUploadUrl } = useApiLogisticsV1LoadsEvidenceUploadCreate();
  const { mutateAsync: processLoadFile } = useApiLogisticsV1LoadsEvidenceUploadUpdate();

  const { mutateAsync: generateTradeCompanySessionEvidenceUploadUrl } =
    useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadCreate();
  const { mutateAsync: processTradeCompanySessionEvidenceFile } =
    useApiQualitySessionsV2TradeCompanySessionsEvidenceUploadUpdate();

  const { mutateAsync: generateExternalEvidenceUploadUrl } = useApiQualityExternalUploadV1EvidenceUploadCreate();
  const { mutateAsync: processExternalEvidenceFile } = useApiQualityExternalUploadV1EvidenceUploadUpdate();

  const { mutateAsync: generateExternalLoadEvidenceUploadUrl } =
    useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadCreate();
  const { mutateAsync: processExternalLoadEvidenceFile } =
    useApiQualityExternalLoadsV1ExternalLoadsEvidenceUploadUpdate();

  const generateUploadUrl = (photo: File, parentId: string) => {
    logger.debug('generateUploadUrl', { mode, parentId, photo });

    if (mode === 'sample') {
      return generateSampleUploadUrl({ data: { content_type: photo.type }, id: Number(parentId) });
    }

    if (mode === 'inspector') {
      return generateInspectionUploadUrl({
        data: { content_type: photo.type },
        slug: parentId,
      });
    }

    if (mode === 'site-material') {
      return generateSiteMaterialUploadUrl({
        data: { content_type: photo.type },
        siteMaterialId: parentId,
      });
    }

    if (mode === 'load') {
      return generateLoadUploadUrl({
        data: { content_type: photo.type },
        loadId: Number(parentId),
      });
    }

    if (mode === 'trade-company-session') {
      return generateTradeCompanySessionEvidenceUploadUrl({
        data: { content_type: photo.type },
        tradeCompanySessionId: Number(parentId),
      });
    }

    if (mode === 'external-evidence') {
      return generateExternalEvidenceUploadUrl({
        data: { content_type: photo.type },
        uuid: parentId,
      });
    }

    if (mode === 'external-load') {
      return generateExternalLoadEvidenceUploadUrl({
        data: { content_type: photo.type },
        externalLoadId: Number(parentId),
      });
    }

    throw new Error('Invalid evidence mode');
  };

  const registerEvidence = (
    file_id: string,
    parentId: string,
    model?: string,
    evidenceType: EvidenceTypeEnum = EvidenceTypeEnum.BP,
    metadata?: Record<string, unknown>,
    process_async?: boolean
  ) => {
    logger.debug('registerEvidence', { evidenceType, file_id, mode, model, parentId });

    if (mode === 'sample') {
      return processSampleFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            inspection_slug: metadata?.inspection_slug,
            origin: metadata?.origin || 'sample',
          },
          model,
          process_async,
        },
        id: Number(parentId),
      });
    }
    if (mode === 'inspector') {
      return processInspectionFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            inspection_slug: parentId,
            origin: 'inspection',
          },
          process_async,
        },
        slug: parentId,
      });
    }

    if (mode === 'site-material') {
      return processSiteMaterialFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'site-material',
          },
          model,
          process_async,
        },
        siteMaterialId: parentId,
      });
    }

    if (mode === 'load') {
      return processLoadFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'load',
          },
          model,
          process_async,
        },
        loadId: Number(parentId),
      });
    }

    if (mode === 'trade-company-session') {
      return processTradeCompanySessionEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'trade-company-session',
          },
          model,
          process_async,
        },
        tradeCompanySessionId: Number(parentId),
      });
    }

    if (mode === 'external-evidence') {
      return processExternalEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'external-evidence',
          },
          model,
          process_async,
        },
        uuid: parentId,
      });
    }

    if (mode === 'external-load') {
      return processExternalLoadEvidenceFile({
        data: {
          evidence_type: evidenceType,
          file_id,
          metadata: {
            ...metadata,
            origin: 'external-load',
          },
          model,
          process_async,
        },
        externalLoadId: Number(parentId),
      });
    }

    throw new Error('Invalid evidence mode');
  };

  return useMutation({
    ...mutationOptions,
    mutationFn: async (data: UploadEvidenceData) => {
      const { photo, onProgress, parentId, model, metadata, processAsync } = data;
      const { upload_url, file_id } = await generateUploadUrl(photo, parentId);

      logger.info('Uploading evidence', {
        evidenceType: data.evidenceType,
        fileName: photo.name,
        parentId,
        upload_url,
      });

      const uploadStartTime = Date.now();

      await uploadFile({
        file: photo,
        headers: { 'content-type': photo.type },
        onProgress,
        url: upload_url,
      });

      logger.info('Evidence uploaded; analysing', {
        evidenceType: data.evidenceType,
        fileName: photo.name,
        parentId,
        upload_url,
      });

      const fileUploadedTime = Date.now();
      const analysis = await registerEvidence(file_id, parentId, model, data.evidenceType, metadata, processAsync);
      const fileProcessingDone = Date.now();
      const metrics = prepareUploadMetrics(
        photo,
        data.startTime,
        uploadStartTime,
        fileUploadedTime,
        fileProcessingDone
      );

      logger.info('Evidence analysis completed', {
        analysis,
        evidenceType: data.evidenceType,
        fileName: photo.name,
        metrics,
        parentId,
        upload_url,
      });

      uploadData({ data: metrics, id: analysis.image.id });

      return analysis;
    },
  });
};
